<template>
  <div
    class="product-card"
    :class="{
      [$class('loading')]: !activeProduct,
      [$class('active')]: showSecondaryImage,
      'product-card--no-wishlist': !wishlist
    }"
    @mouseenter="loadSecondaryImage = true"
  >
    <div class="product-card__wrapper">
      <a
        class="product-card__body"
        :href="url"
        @click="trackClick"
        @focus="loadSecondaryImage = true"
      >
        <span
          class="visually-hidden"
          v-text="activeProduct.title"
        />

        <div class="product-card__image-container">
          <span
            v-if="productBadge"
            class="product-card__badge text-other-label-small"
            v-text="productBadge"
          />

          <responsive-image
            class="product-card__primary-image"
            :image="activeProduct.featuredImage"
            :min-max="imageConfig.minMax"
            :ratio="imageConfig.ratio"
            :use-image-loading="useImageLoading"
            :lazyload="lazyload"
            :sizes="imageConfig.sizes"
          />
          <responsive-image
            v-if="loadSecondaryImage && activeProduct.secondaryImage"
            class="product-card__secondary-image"
            :image="activeProduct.secondaryImage"
            :min-max="imageConfig.minMax"
            :padding="false"
            :use-image-loading="useImageLoading"
            :sizes="imageConfig.sizes"
            @image-loaded="showSecondaryImage = true"
          />
        </div>

        <div class="product-card__footer is-desktop">
          <p
            class="
              product-card__title
              text-body-s-regular
            "
            v-text="displayTitle"
          />

          <product-prices
            class="product-card__prices"
            :available="priceSource?.available"
            :compare-at-price="priceSource?.compareAtPrice?.amount"
            :price="priceSource?.price?.amount"
          />
        </div>
      </a>

      <quick-add-drawer-trigger
        v-if="!quickAdd && quickAddDrawer && display"
        :uid="uid"
        class="product-card__quick-add-drawer-trigger"
        :product="activeProduct"
        :variant="variant"
      />
    </div>

    <a
      class="product-card__footer is-mobile"
      :href="url"
      @click="trackClick"
    >
      <p
        class="
          product-card__title
          text-body-s-regular
        "
        v-text="displayTitle"
      />

      <product-prices
        class="product-card__prices"
        :available="priceSource?.available"
        :compare-at-price="priceSource?.compareAtPrice?.amount"
        :price="priceSource?.price?.amount"
      />
    </a>

    <!-- Quick add v2 - dropdown version -->
    <quick-add
      v-if="quickAdd && !quickAddDrawer && activeProduct && display"
      class="product-card__quick-add"
      :product="activeProduct"
      :variant-id="variantId"
      @update-variant="handleVariantUpdate"
      @add-cart="payload => $emit('add-cart', payload)"
    />
  </div>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} [useImageLoading] - Use image on load animations?
 * @param {Boolean} [lazyload] - Lazyload product image?
 * @param {String} [imageSizes] - Responsive image sizes for where the card
 * is being used.
 * @param {String} [source] - Track view item click with this source prefixed.
 * @param {function} [sendEvent] - Send event function from algolia
 */
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import QuickAdd from '~async/quick-add/quick-add'
import QuickAddDrawerTrigger from '~async/quick-add-drawer/quick-add-drawer-trigger'
import breakpoints from '~/config/breakpoints'

import productQuery from '~graphql/product.gql'

import { getShortTitle } from '~/helpers/product'
import { emitAddedLineItem, emitUpdateVariant } from '~/helpers/vue'
import { formatEcommerceEvent, track } from '~/helpers/datalayer-actions'
import { formatProduct } from '~/helpers/format'
import { getMetafieldIDs, getVariables } from '~/helpers/graphql'
import { generateUUID } from '~/helpers/general'
import { handleAlgoliaClick } from '~/helpers/algolia'

export default {
  name: 'ProductCard',

  components: {
    QuickAdd,
    ProductPrices,
    ResponsiveImage,
    QuickAddDrawerTrigger,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    useImageLoading: {
      type: Boolean,
      default: true,
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    imageOptions: {
      type: Object,
      default: () => ({}),
    },
    wishlist: {
      type: Boolean,
      default: true,
    },
    variantId: {
      type: Number,
      default: null,
    },
    quickAdd: {
      type: Boolean,
      default: false,
    },
    quickAddDrawer: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: '',
    },
    sendEvent: {
      type: Function,
      default: null,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
    'add-cart': emitAddedLineItem,
  },

  data() {
    return {
      uid: generateUUID(),
      loadSecondaryImage: false,
      showSecondaryImage: false,
      imageConfig: {
        sizes: `(min-width: ${breakpoints.l}) 22vw, 50vw`,
        minMax: '175-500',
        ratio: '1:1',
        ...this.imageOptions,
      },
      selectedVariant: this.variantId,
      shopifyProduct: null,
      display: true,
    }
  },

  computed: {

    /**
     * Get badge from product tags.
     * @returns {String}
     */
    productBadge() {
      if (!this.activeProduct.tags) {
        return null
      }

      const badge = this.activeProduct.tags.filter((tag) => tag.includes('badge:'))
      return badge[0]?.replace('badge:', '')
    },

    /**
     * Get short title or fallback to the default title
     * @returns {String}
     */
    displayTitle() {
      return getShortTitle(this.activeProduct)
    },

    /**
     * Compute price source. Use variant if possible
     * @returns {boolean|Object}
     */
    priceSource() {
      return this.variant || this.activeProduct
    },

    /**
     * Compute product URL
     * @returns {string}
     */
    url() {
      if (!this.activeProduct) {
        return ''
      }
      const url = new URL(this.activeProduct.url, window.origin)
      const params = new URLSearchParams(url.search)

      if (this.selectedVariant) {
        params.set('variant', this.selectedVariant)
      }

      if (!params.toString()) {
        return url.pathname
      }

      return `${url.pathname}?${params.toString()}`
    },

    /**
     * Compute variant based on provided selected variant ID
     * @returns {Object|null}
     */
    variant() {
      if (!this.activeProduct?.variants) {
        return null
      }
      return this.activeProduct.variants.find(
        (pVariant) => pVariant.id === this.selectedVariant,
      )
    },

    /**
     * If product is an algolia product, return the queried Shopify product
     */
    activeProduct() {
      return this.product.algolia && this.shopifyProduct
        ? this.shopifyProduct
        : this.product
    },
  },

  /**
   * If product is from Algolia, query Shopify product as well
   */
  created() {
    if (this.product.algolia !== true) {
      return
    }

    this.queryProduct()
  },

  methods: {

    /**
     * Emit variant update
     * @param variant - Formatted variant object
     */
    handleVariantUpdate(variant) {
      this.$emit('update-variant', variant)
      if (variant) {
        this.selectedVariant = variant.id
      }
    },

    /**
     * Track product card click using the source
     */
    trackClick() {
      // Algolia conversions
      handleAlgoliaClick(this.product)
      if (typeof this.sendEvent === 'function' && this.product.algolia) {
        this.sendEvent('click', this.product, 'Product Clicked')
      }

      if (!this.source) {
        return
      }

      // GTM / GA view event
      track({
        event: `${this.source}_view_item`,
        ...formatEcommerceEvent({
          product: this.activeProduct,
          variant: this.variant,
        }),
      })
    },

    /**
     * Query Shopify product
     * - Used if the product prop was provided by Algolia
     */
    async queryProduct() {
      try {
        const response = await cnvs.Query(
          {
            query: productQuery,
            variables: getVariables({
              handle: this.product.handle,
              metafieldIds: [
                ...getMetafieldIDs('product'),
                ...getMetafieldIDs('variant'),
              ],
            }),
          },
          false,
        )

        if (!response?.product) {
          this.display = false
          cnvs.ReportError('[Product Card - No response.product]', this.product)
          return
        }

        this.shopifyProduct = formatProduct(response)
      } catch (error) {
        cnvs.ReportError('[Product Card - Error]', error)
      }
    },
  },

}
</script>

<style lang="scss">
@import './product-card';
</style>
